import { motion } from "framer-motion";
import { Truck, Timer, Stethoscope, GraduationCap, Award } from "lucide-react";
import { IMAGES } from "@/data/catalog";

const fade = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const ChapterLabel = ({ num, title }) => (
  <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-8">
    <span className="font-mono-med text-sm text-[#D92638] border border-[#D92638]/40 rounded-full px-3.5 py-1">{num}</span>
    <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">{title}</span>
    <span className="h-px flex-1 bg-slate-200" />
  </motion.div>
);

export default function Chapters() {
  return (
    <section id="why" className="relative bg-[#F8FAFC] dot-grid-dark py-20 lg:py-28" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-28">
        {/* Chapter 01 */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <ChapterLabel num="01" title="The Turnaround Crisis" />
            <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] leading-tight">
              A leukemia diagnosis shouldn't wait <span className="font-mono-med text-[#D92638]">3-7 days</span> for a courier to Hyderabad.
            </motion.h2>
            <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-6 text-base text-slate-600 leading-relaxed max-w-2xl">
              Today, almost every lab in Visakhapatnam sends bone marrow and flow cytometry samples 300-600 km away - to Hyderabad, Chennai or Bengaluru. Patients pay ₹800-2,000 extra in courier costs, samples risk degradation in transit, and oncologists lose nearly a week before treatment can begin.
            </motion.p>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Truck, t: "300-600 km", s: "distance samples travel today" },
                { icon: Timer, t: "24-48 hrs", s: "Hemozy in-house turnaround" },
                { icon: Stethoscope, t: "6-8", s: "Vizag hospitals treating blood cancers now" },
              ].map(({ icon: Icon, t, s }) => (
                <div key={t} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)] hover:shadow-[0_12px_32px_-6px_rgba(15,25,44,0.12)] hover:-translate-y-1 transition-all duration-300">
                  <Icon className="w-5 h-5 text-[#D92638]" />
                  <div className="font-mono-med text-xl font-semibold text-[#0B192C] mt-3">{t}</div>
                  <div className="text-xs text-slate-500 mt-1">{s}</div>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="lg:col-span-5 relative">
            <div className="rounded-[2rem] overflow-hidden border border-slate-200 shadow-[0_24px_60px_-16px_rgba(11,25,44,0.25)]">
              <img src={IMAGES.lab} alt="Hemozy in-house laboratory" className="w-full h-[360px] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="absolute -bottom-5 left-6 bg-[#0B192C] text-white rounded-2xl px-5 py-3.5 shadow-xl">
              <span className="font-mono-med text-sm">Same city. Same specialist. <span className="text-[#14B8A6]">48 hrs max.</span></span>
            </div>
          </motion.div>
        </div>

        {/* Chapter 02 */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="lg:col-span-5 order-2 lg:order-1 relative">
            <div className="rounded-[2rem] overflow-hidden border border-slate-200 shadow-[0_24px_60px_-16px_rgba(11,25,44,0.25)] relative">
              <img src={IMAGES.doctor} alt="Dr. Anannya Bhakat - Lab Director, Hemozy" className="w-full h-[480px] object-cover object-[50%_22%] hover:scale-105 transition-transform duration-700" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0B192C]/90 to-transparent px-6 pt-14 pb-5">
                <div className="text-white font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dr. Anannya Bhakat</div>
                <div className="text-[#14B8A6] text-xs font-medium tracking-wide">Lab Director & Consultant Pathologist · PDCC Hematopathology, CMC Vellore</div>
              </div>
            </div>
            <div className="absolute -top-4 -right-3 bg-[#D92638] text-white text-xs font-semibold rounded-full px-4 py-2 shadow-[0_0_25px_rgba(217,38,56,0.35)]">
              A category of one
            </div>
          </motion.div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <ChapterLabel num="02" title="Expert Leadership" />
            <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] leading-tight">
              Led by a <span className="text-[#0D9488]">CMC Vellore</span>-trained hematopathologist. The only one in Vizag.
            </motion.h2>
            <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-6 text-base text-slate-600 leading-relaxed max-w-2xl">
              Dr. Anannya Bhakat's pathway - MD Pathology from AIIMS Bhubaneswar, DNB, then PDCC in Hematopathology from CMC Vellore - is rare even in metro India, and unavailable anywhere else in Visakhapatnam's diagnostic ecosystem. Every blood report at Hemozy carries a specialist's eye.
            </motion.p>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-8 space-y-3">
              {[
                { icon: GraduationCap, t: "MBBS → MD Pathology, AIIMS Bhubaneswar" },
                { icon: Award, t: "DNB + PDCC Hematopathology, CMC Vellore" },
                { icon: Stethoscope, t: "Lab Director & Consultant Pathologist, Hemozy" },
              ].map(({ icon: Icon, t }) => (
                <div key={t} className="flex items-center gap-3.5 bg-white rounded-xl border border-slate-200/90 px-5 py-3.5 hover:border-[#0D9488]/50 hover:-translate-y-0.5 transition-all duration-300">
                  <div className="w-9 h-9 rounded-lg bg-[#0D9488]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4.5 h-4.5 w-5 h-5 text-[#0D9488]" />
                  </div>
                  <span className="text-sm lg:text-base font-medium text-slate-700">{t}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
